import React, { useState } from 'react'
import styled from 'styled-components'
import { useParams } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import Typography from '@/v2/components/common/Typography'
import { useMessagesContext } from '@/v2/contexts/MessagesContext'
import { useConversation } from '@/v2/hooks/messages/useConversations'
import { useRescheduleLink } from '@/v2/hooks/messages/useRescheduleLink'
import { safeFormat } from '@/v2/utils/convert'

const APPOINTMENT_FORMAT = 'EEE d MMM · h:mm a'

// What the team can do about each block. The API already sends a sentence for
// the patient-facing half of the story; this is the half it cannot know. It
// serves two places now: the box that opens after a click, and the button
// itself, which the conversation already knows the answer for.
const NEXT_STEP = {
  disabled:
    'Rescheduling by link is switched off. An admin can turn it on in Settings → Rescheduling.',
  patient_not_linked: 'Assign a patient to this conversation and try again.',
  patient_ambiguous:
    'This conversation has more than one patient. Assign the one you are replying to.',
  conversation_disabled:
    'Rescheduling is turned off for this conversation. Turn it back on to send a link.',
  no_upcoming_appointment:
    'There is nothing to move — book the appointment first.',
  no_booking_rules:
    'The appointment type has no reschedule link hours yet. Set them in Settings → Appointment types.',
  no_availability:
    'No time can be offered inside the configured horizon. Agree on a time by hand, or widen the horizon in Settings → Rescheduling.',
}

const Wrapper = styled.div`
  padding: 4px 0 0;
`

const Action = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: none;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.link.main};
  cursor: pointer;

  &:disabled {
    color: #9ca3af;
    cursor: default;
  }

  &:hover:not(:disabled) {
    text-decoration: underline;
  }
`

const Hint = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: #6b7280;
`

const Panel = styled.div`
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: 4px;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
`

const Blocked = styled(Panel)`
  background-color: #fef3c7;
  border-color: #fde68a;
`

const AppointmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
`

const AppointmentOption = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  text-align: left;

  &:hover {
    border-color: #2563eb;
  }
`

const Dismiss = styled.button`
  border: none;
  background: none;
  padding: 0;
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

const RescheduleLinkButton = () => {
  const theme = useTheme()
  const { conversationId } = useParams()
  const { onSetMessage } = useMessagesContext()
  const { conversation } = useConversation(conversationId)
  const { prepareRescheduleLink, isLoading: isPreparing } = useRescheduleLink(
    conversationId
  )

  const [blocked, setBlocked] = useState(null)
  const [choices, setChoices] = useState([])
  const [reused, setReused] = useState(false)

  // Costs no request: the page around this button already fetched the
  // conversation under the same react-query key, and the WebSocket broadcast
  // writes into it, so the button follows the thread without a refresh. Only
  // the cheap half of the guards is answered here — the click still has the
  // last word about the calendar and about which appointment to move.
  const rescheduleLink = conversation?.rescheduleLink
  const blockedReason = rescheduleLink?.reason

  const prepare = async appointmentId => {
    setBlocked(null)
    setChoices([])
    setReused(false)

    try {
      const reply = await prepareRescheduleLink(appointmentId)
      onSetMessage(reply.body)
      setReused(reply.reused)
    } catch (error) {
      if (error.reason === 'appointment_ambiguous') {
        setChoices(error.appointments)
      }

      setBlocked({ message: error.message, reason: error.reason })
    }
  }

  // The only reason that has nothing to do with this conversation: the clinic
  // has rescheduling off, so there is no team action to offer here and the
  // button has no business on the screen.
  if (blockedReason === 'disabled') return null

  // The other reasons are actionable — assign a patient, book an appointment —
  // so the button stays, greyed, saying what is missing instead of waiting for
  // a click to fail. A conversation still loading has no answer yet, and the
  // click stays the authority, exactly as it was before the field existed.
  const cannotPrepare = !!rescheduleLink && !rescheduleLink.available

  return (
    <Wrapper>
      <Action
        type="button"
        theme={theme.palette}
        onClick={() => prepare()}
        disabled={isPreparing || cannotPrepare}
      >
        <EventRepeatIcon sx={{ fontSize: 16 }} />
        {isPreparing ? 'Preparing...' : 'Send reschedule link'}
      </Action>

      {cannotPrepare && NEXT_STEP[blockedReason] && (
        <Hint>{NEXT_STEP[blockedReason]}</Hint>
      )}

      {reused && (
        <Panel>
          <Typography fontSize="12px">
            This is the link the patient already received — it is still valid,
            so it was not replaced.
          </Typography>
        </Panel>
      )}

      {blocked && (
        <Blocked>
          <Typography fontSize="12px">{blocked.message}</Typography>
          {NEXT_STEP[blocked.reason] && (
            <Typography fontSize="12px" sx={{ mt: '4px' }}>
              {NEXT_STEP[blocked.reason]}
            </Typography>
          )}

          {choices.length > 0 && (
            <AppointmentList>
              {choices.map(appointment => (
                <AppointmentOption
                  key={appointment.id}
                  type="button"
                  onClick={() => prepare(appointment.id)}
                >
                  <Typography fontSize="13px" bold>
                    {safeFormat(appointment.startsAt, APPOINTMENT_FORMAT)}
                  </Typography>
                  <Typography fontSize="12px">
                    {`${appointment.appointmentType?.title ||
                      'Appointment'} · ${appointment.duration} min`}
                  </Typography>
                </AppointmentOption>
              ))}
            </AppointmentList>
          )}

          <Dismiss type="button" onClick={() => setBlocked(null)}>
            Dismiss
          </Dismiss>
        </Blocked>
      )}
    </Wrapper>
  )
}

export default RescheduleLinkButton
